import React, { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { Checkbox, ContentBlockHeader } from '@/components';
import StepFooter from './StepFooter';
import { CspRegistrationFormData } from '../types';
import { faFileContract } from '@fortawesome/pro-regular-svg-icons';
import PDFViewer from '@/shared_elements/containers/PDFViewer';
import Divider from './Divider';

interface StepTermsConditionsProps {
  onBack: () => void;
  paymentInfo?: {
    title: string;
    text: string;
  };
  loading?: boolean;
}

const StepTermsConditions: React.FC<StepTermsConditionsProps> = ({
  onBack,
  loading = false,
}) => {
  const { watch, setValue } = useFormContext<CspRegistrationFormData>();
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const agreeTerms = watch('ui.agreeTerms');
  const market = watch('register.market');
  const isDeMarket = market === 'DE';

  const pdfRef = useRef<HTMLDivElement>(null);

  const pdfUrl = 'https://www.campaignregistry.com/TCR-T&Cs.pdf';

  const handleAgreeTerms = (checked: boolean) => {
    setValue('ui.agreeTerms', checked, { shouldValidate: true });
    if (checked) {
      const acceptTime = `${new Date().toISOString().slice(0, -5)}Z`;
      setValue('ui.termsAcceptDate', acceptTime);
    } else {
      setValue('ui.termsAcceptDate', '');
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, px: 24 }}>
        <ContentBlockHeader
          icon={faFileContract}
          title="Terms & Conditions"
          subTitle={
            isDeMarket
              ? 'Once CSP verification is completed successfully, approved CSPs can immediately begin registering brands.'
              : 'A one time, non-refundable payment of $200.00 is required to begin CSP verification. Once completed successfully, approved CSPs can immediately begin registering brands and campaigns.'
          }
          titleRowSx={{ height: '32px', mb: 24 }}
        />

        {/* Outer scrollable container */}
        <Box
          ref={pdfRef}
          data-testid="cspPaymentInfo"
          sx={{
            border: '1px solid #EAF0F2',
            borderRadius: '4px',
            height: '450px',
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 16,
          }}
        >
          {/* Terms & Conditions PDF */}
          {!isDeMarket ? (
            <PDFViewer
              url={pdfUrl}
              width={780}
              onScrollToBottom={() => setScrollToBottom(true)}
              customRef={pdfRef}
              sx={{ height: 'auto', overflowY: 'visible', overflowX: 'hidden' }}
            />
          ) : (
            <Box
              sx={{
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          )}

          <Divider sx={{ my: 16 }} />

          {/* Checkbox Container */}
          <Box
            sx={{
              backgroundColor: '#E6F4F7',
              border: '1px solid #EAF0F2',
              borderRadius: '8px',
              p: '16px',
              width: 444,
              mx: 'auto',
              mb: 16,
            }}
          >
            <Checkbox
              data-testid="termsAgreementCheckbox"
              label="I agree to the Terms and Conditions"
              checked={agreeTerms}
              onChange={handleAgreeTerms}
              disabled={!scrollToBottom}
              sx={{ '& div~div': { color: '#19262A' } }}
            />
          </Box>
        </Box>
      </Box>

      {/* Scroll instruction text */}
      <Box
        data-testid="scrollInstructionText"
        sx={{
          mt: 16,
          mx: 24,
          fontSize: '14px',
          color: '#49535D',
          fontWeight: 500,
        }}
      >
        Scroll to read terms and acknowledge.
      </Box>

      <StepFooter
        onBack={onBack}
        nextLabel="Submit Registration"
        nextDisabled={isDeMarket || !agreeTerms || loading}
        hideDivider
      />
    </Flex>
  );
};

export default StepTermsConditions;
